import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Helper component to fix tile loading issues by calling invalidateSize
 * after the map mounts and on window resize.
 */
const MapInvalidateSizeFix = () => {
  const map = useMap();

  useEffect(() => {
    let frameId = null;
    let resizeObserver = null;
    let settleTimers = [];

    const invalidate = () => {
      // Do not re-center while simply resizing the map container.
      map.invalidateSize({ pan: false });
    };

    const scheduleInvalidate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        invalidate();
      });
    };

    const scheduleSettledInvalidates = () => {
      settleTimers.forEach((id) => clearTimeout(id));
      settleTimers = [40, 120, 260].map((delay) =>
        setTimeout(() => {
          scheduleInvalidate();
        }, delay),
      );
    };

    // Small delay to ensure container is fully rendered
    const timeout = setTimeout(() => {
      scheduleInvalidate();
      scheduleSettledInvalidates();
    }, 100);

    const handleResize = () => {
      scheduleInvalidate();
      scheduleSettledInvalidates();
    };

    window.addEventListener('resize', handleResize);

    const container = typeof map.getContainer === 'function' ? map.getContainer() : null;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleInvalidate();
      });
      resizeObserver.observe(container);
    }

    return () => {
      clearTimeout(timeout);
      settleTimers.forEach((id) => clearTimeout(id));
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);

  return null;
};

export default MapInvalidateSizeFix;
